import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import QuizIcon from '@mui/icons-material/Quiz';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { fetchAssessments, createAssessment, updateAssessment, deleteAssessment } from '../../api/assessments';
import AssessmentFormDialog from '../forms/AssessmentFormDialog';
import ConfirmDialog from '../feedback/ConfirmDialog';
import ExpandableText from '../common/ExpandableText';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };

const typeLabels = {
  ONLINE_TEST: 'Онлайн-тест',
  PRACTICAL: 'Практичні завдання',
  ORAL: 'Усне опитування',
  OTHER: 'Інше',
};

const ScoreBadge = ({ label, value }) => {
  if (value == null) return <Typography variant="body2" sx={{ color: '#8891A5' }}>—</Typography>;
  const color = value >= 8 ? '#22C55E' : value >= 5 ? '#F59E0B' : '#EF4444';
  return (
    <Chip
      label={`${label}: ${value}`}
      size="small"
      sx={{ bgcolor: `${color}18`, color, fontWeight: 700, fontSize: 12, height: 26 }}
    />
  );
};

const Field = ({ label, value, lines = 2 }) => {
  if (!value) return null;
  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography variant="caption" sx={{ color: B.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10 }}>
        {label}
      </Typography>
      <ExpandableText lines={lines} sx={{ mt: 0.25 }}>{value}</ExpandableText>
    </Box>
  );
};

const AssessmentTable = ({ studentId, canEdit = false }) => {
  const queryClient = useQueryClient();
  const isNarrow = useMediaQuery('(max-width:900px)');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const assessmentsQuery = useQuery({
    queryKey: ['assessments', studentId],
    queryFn: () => fetchAssessments(studentId),
    enabled: Boolean(studentId),
  });

  const createMutation = useMutation({
    mutationFn: (payload) => createAssessment({ ...payload, studentId: Number(studentId) }),
    onSuccess: () => { enqueueSnackbar('Контроль додано', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['assessments', studentId] }); },
    onError: () => enqueueSnackbar('Не вдалося додати', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateAssessment(id, payload),
    onSuccess: () => { enqueueSnackbar('Контроль оновлено', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['assessments', studentId] }); },
    onError: () => enqueueSnackbar('Не вдалося оновити', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteAssessment(id),
    onSuccess: () => { enqueueSnackbar('Контроль видалено', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['assessments', studentId] }); },
    onError: () => enqueueSnackbar('Не вдалося видалити', { variant: 'error' }),
  });

  const handleSubmit = async (values) => {
    if (selected) {
      await updateMutation.mutateAsync({ id: selected.id, payload: values });
    } else {
      await createMutation.mutateAsync(values);
    }
  };

  const data = assessmentsQuery.data || [];

  return (
    <>
      <Card sx={{ ...cardSx, overflow: 'hidden', border: `2px solid ${B.primary}` }}>
        {/* Header */}
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2, bgcolor: '#F8F7FF', borderBottom: '2px solid #EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: B.dark, lineHeight: 1.2 }}>Контроль знань</Typography>
              <Typography variant="caption" sx={{ color: '#8891A5' }}>Результати тестувань протягом занять</Typography>
            </Box>
          </Stack>
          {canEdit && (
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              onClick={() => { setSelected(null); setDialogOpen(true); }}
              sx={{ bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, textTransform: 'none', fontWeight: 600 }}
            >
              Додати
            </Button>
          )}
        </Box>

        {assessmentsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: B.primary }} /></Box>
        ) : data.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#8891A5' }}>Контрольних робіт поки немає.</Typography>
          </Box>
        ) : (
          isNarrow ? (
            <Box>
              {data.map((a, idx) => (
                <Box
                  key={a.id}
                  sx={{ px: 2, py: 1.75, borderBottom: '1px solid #F1F0FB', bgcolor: idx % 2 === 0 ? '#fff' : '#FAFAFF', '&:last-of-type': { borderBottom: 'none' } }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                    <Box sx={{ flexShrink: 0, width: 30, height: 30, borderRadius: 2, bgcolor: B.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
                      {String(data.length - idx).padStart(2, '0')}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: B.dark, lineHeight: 1.3 }}>
                        {dayjs(a.date).format('DD.MM.YYYY')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#8891A5' }}>
                        {dayjs(a.date).format('dddd')}
                        {a.duration && <>, {a.duration}</>}
                      </Typography>
                    </Box>
                    {canEdit && (
                      <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                        <IconButton size="small" aria-label="Редагувати" onClick={() => { setSelected(a); setDialogOpen(true); }} sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: B.primary } }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" aria-label="Видалити" onClick={() => setConfirmState(a)} sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: '#EF4444' } }}><DeleteIcon fontSize="small" /></IconButton>
                      </Stack>
                    )}
                  </Stack>

                  {a.type && (
                    <Chip label={typeLabels[a.type] || a.type} size="small" sx={{ mt: 1, bgcolor: '#EDE9FE', color: B.primary, fontWeight: 600, fontSize: 12, height: 24 }} />
                  )}

                  <Field label="Теми" value={a.topics} />
                  <Field label="Завдання" value={a.taskDescription} />
                  <Field label="Примітки" value={a.notes} />

                  <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    <ScoreBadge label="Теорія" value={a.theoryScore} />
                    <ScoreBadge label="Практика" value={a.practiceScore} />
                    <ScoreBadge label="Підсумок" value={a.finalScore} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
          <TableContainer>
            <Table sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8F7FF', '& th': { color: B.primary, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center', borderBottom: '2px solid #EDE9FE', borderRight: '1px solid #EDE9FE', py: 1.8, px: 2.5, '&:last-child': { borderRight: 'none' } } }}>
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell sx={{ width: 150 }}>Теми</TableCell>
                  <TableCell sx={{ width: '30%' }}>Завдання</TableCell>
                  <TableCell align="center">Оцінки</TableCell>
                  <TableCell>Примітки</TableCell>
                  {canEdit && <TableCell align="right">Дії</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((a, idx) => (
                  <TableRow
                    key={a.id}
                    sx={{
                      transition: 'all 0.15s',
                      bgcolor: idx % 2 === 0 ? '#fff' : '#FAFAFF',
                      '&:hover': { bgcolor: '#EDE9FE' },
                      '& td': { py: 2, px: 2.5, borderBottom: '1px solid #F1F0FB', borderRight: '1px solid #F1F0FB', '&:last-child': { borderRight: 'none' } },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: B.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                        {String(data.length - idx).padStart(2, '0')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }}>{dayjs(a.date).format('DD.MM.YYYY')}</Typography>
                        <Typography variant="caption" sx={{ color: '#8891A5' }}>{dayjs(a.date).format('dddd')}</Typography>
                        {a.duration && <Typography variant="caption" sx={{ color: '#8891A5' }}>{a.duration}</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {a.type ? (
                        <Chip label={typeLabels[a.type] || a.type} size="small" sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 600, fontSize: 12, height: 24 }} />
                      ) : (
                        <Typography variant="body2" sx={{ color: '#8891A5', fontStyle: 'italic' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography lang="uk" variant="body2" fontWeight={700} sx={{ color: B.dark, hyphens: 'auto', overflowWrap: 'break-word', wordBreak: 'normal' }}>
                        {a.topics || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {a.taskDescription ? (
                        <ExpandableText lines={4} sx={{ bgcolor: '#F8F7FF', border: '1px solid #EDE9FE', borderRadius: 2, px: 1.5, py: 0.75 }}>
                          {a.taskDescription}
                        </ExpandableText>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#8891A5', fontStyle: 'italic' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack spacing={0.5} alignItems="center">
                        <ScoreBadge label="Теорія" value={a.theoryScore} />
                        <ScoreBadge label="Практика" value={a.practiceScore} />
                        <ScoreBadge label="Підсумок" value={a.finalScore} />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, minWidth: 180 }}>
                      {a.notes ? (
                        <ExpandableText lines={4} sx={{ bgcolor: '#F8F7FF', border: '1px solid #EDE9FE', borderRadius: 2, px: 1.5, py: 0.75 }}>
                          {a.notes}
                        </ExpandableText>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#8891A5', fontStyle: 'italic' }}>Без приміток</Typography>
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                          <Tooltip title="Редагувати">
                            <IconButton size="small" onClick={() => { setSelected(a); setDialogOpen(true); }}
                              sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: B.primary } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Видалити">
                            <IconButton size="small" onClick={() => setConfirmState(a)}
                              sx={{ color: '#8891A5', '&:hover': { color: '#fff', bgcolor: '#EF4444' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )
        )}
      </Card>

      <AssessmentFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        initialValues={{
          date: selected?.date || dayjs().format('YYYY-MM-DD'),
          topics: selected?.topics || '',
          taskDescription: selected?.taskDescription || '',
          notes: selected?.notes || '',
          theoryScore: selected?.theoryScore || 5,
          practiceScore: selected?.practiceScore || 5,
          type: selected?.type || '',
          duration: selected?.duration || '',
        }}
        title={selected ? 'Редагувати контроль' : 'Новий контроль знань'}
      />

      <ConfirmDialog
        open={Boolean(confirmState)}
        title="Видалити контроль знань?"
        description="Цю дію неможливо скасувати."
        onCancel={() => setConfirmState(null)}
        onConfirm={async () => {
          await deleteMutation.mutateAsync(confirmState.id);
          setConfirmState(null);
        }}
      />
    </>
  );
};

export default AssessmentTable;
